/**
 * Registry 管理器 - 管理部署配置的注册和分发
 */

import { Database } from 'better-sqlite3';
import fs from 'fs-extra';
import path from 'path';
import { z } from 'zod';
import type { 
  RegistryItem, 
  SearchQuery, 
  ApplyOptions, 
  RegistryStats,
  EnvironmentVariable 
} from './types.js';

// 验证模式
const registryItemSchema = z.object({
  name: z.string().regex(/^[a-z0-9-]+$/, 'Name must be lowercase alphanumeric with hyphens'),
  type: z.enum(['deployment', 'service', 'config', 'template']),
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Version must follow semver format'),
  description: z.string().min(10).max(200),
  category: z.string(),
  tags: z.array(z.string()),
  files: z.array(z.object({
    path: z.string(),
    content: z.string(),
    type: z.enum(['config', 'script', 'template', 'documentation']),
    encoding: z.enum(['utf8', 'base64']).optional(),
    permissions: z.string().optional()
  })).min(1),
  dependencies: z.record(z.string()).optional(),
  environment: z.record(z.object({
    required: z.boolean(),
    description: z.string(),
    default: z.string().optional(),
    type: z.enum(['string', 'number', 'boolean', 'url', 'path']).optional(),
    pattern: z.string().optional(),
    options: z.array(z.string()).optional()
  })).optional(),
  scripts: z.record(z.string()).optional(),
  metadata: z.object({
    homepage: z.string().url().optional(),
    repository: z.string().url().optional(),
    documentation: z.string().url().optional(),
    examples: z.array(z.string()).optional()
  }).optional(),
  targets: z.array(z.object({
    platform: z.string(),
    requirements: z.array(z.string()).optional(),
    recommended: z.boolean().optional()
  })).optional()
});

export class RegistryManager {
  private db: Database;
  private storagePath: string;

  constructor(db: Database, storagePath: string) {
    this.db = db;
    this.storagePath = storagePath;
    this.initializeDatabase();
    this.ensureStorageDirectory();
  }

  private initializeDatabase(): void {
    // Registry items 表
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS registry_items (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT UNIQUE NOT NULL,
        type TEXT NOT NULL,
        version TEXT NOT NULL,
        description TEXT NOT NULL,
        author TEXT,
        license TEXT,
        category TEXT NOT NULL,
        tags TEXT NOT NULL, -- JSON array
        dependencies TEXT, -- JSON object
        environment TEXT, -- JSON object
        scripts TEXT, -- JSON object
        metadata TEXT, -- JSON object
        targets TEXT, -- JSON array
        downloads INTEGER DEFAULT 0,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );

      CREATE INDEX IF NOT EXISTS idx_registry_name ON registry_items(name);
      CREATE INDEX IF NOT EXISTS idx_registry_type ON registry_items(type);
      CREATE INDEX IF NOT EXISTS idx_registry_category ON registry_items(category);
    `);

    // Registry 使用记录表
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS registry_usage (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        item_name TEXT NOT NULL,
        project_id TEXT,
        applied_at TEXT NOT NULL,
        variables TEXT, -- JSON object
        FOREIGN KEY (item_name) REFERENCES registry_items(name)
      );
    `);
  }

  private ensureStorageDirectory(): void {
    fs.ensureDirSync(this.storagePath);
    fs.ensureDirSync(path.join(this.storagePath, 'items'));
  }

  /**
   * 发布新的 Registry 项目
   */
  async publish(item: Omit<RegistryItem, 'createdAt' | 'updatedAt'>): Promise<RegistryItem> {
    // 验证输入
    const validation = registryItemSchema.safeParse(item);
    if (!validation.success) {
      throw new Error(`验证失败: ${validation.error.message}`);
    }

    const now = new Date().toISOString();
    const registryItem: RegistryItem = {
      ...item,
      createdAt: now,
      updatedAt: now
    };

    // 检查是否已存在
    const existing = this.db.prepare('SELECT id FROM registry_items WHERE name = ?').get(item.name);
    if (existing) {
      throw new Error(`Registry item '${item.name}' already exists`);
    }

    // 保存到数据库
    const stmt = this.db.prepare(`
      INSERT INTO registry_items (
        name, type, version, description, author, license,
        category, tags, dependencies, environment, scripts,
        metadata, targets, created_at, updated_at
      ) VALUES (
        ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?
      )
    `);

    stmt.run(
      registryItem.name,
      registryItem.type,
      registryItem.version,
      registryItem.description,
      registryItem.author || null,
      registryItem.license || null,
      registryItem.category,
      JSON.stringify(registryItem.tags),
      JSON.stringify(registryItem.dependencies || {}),
      JSON.stringify(registryItem.environment || {}),
      JSON.stringify(registryItem.scripts || {}),
      JSON.stringify(registryItem.metadata || {}),
      JSON.stringify(registryItem.targets || []),
      registryItem.createdAt,
      registryItem.updatedAt
    );

    // 保存文件内容
    const itemPath = path.join(this.storagePath, 'items', registryItem.name);
    fs.ensureDirSync(itemPath);
    
    // 保存完整的 item 数据
    fs.writeJsonSync(path.join(itemPath, 'registry-item.json'), registryItem, { spaces: 2 });
    
    // 保存各个文件
    for (const file of registryItem.files) {
      const filePath = path.join(itemPath, 'files', file.path);
      fs.ensureDirSync(path.dirname(filePath));
      
      if (file.encoding === 'base64') {
        fs.writeFileSync(filePath, Buffer.from(file.content, 'base64'));
      } else {
        fs.writeFileSync(filePath, file.content, 'utf8');
      }
      
      // 设置文件权限
      if (file.permissions) {
        fs.chmodSync(filePath, file.permissions);
      }
    }

    return registryItem;
  }

  /**
   * 获取 Registry 项目
   */
  async get(name: string): Promise<RegistryItem | null> {
    const row = this.db.prepare(`
      SELECT * FROM registry_items WHERE name = ?
    `).get(name) as any;

    if (!row) {
      return null;
    }

    // 从文件系统加载完整数据
    const itemPath = path.join(this.storagePath, 'items', name, 'registry-item.json');
    if (fs.existsSync(itemPath)) {
      return fs.readJsonSync(itemPath) as RegistryItem;
    }

    // 如果文件不存在，从数据库重建
    return this.rowToRegistryItem(row);
  }

  /**
   * 搜索 Registry 项目
   */
  async search(query: SearchQuery): Promise<RegistryItem[]> {
    let sql = 'SELECT * FROM registry_items WHERE 1=1';
    const params: any[] = [];

    if (query.query) {
      sql += ' AND (name LIKE ? OR description LIKE ?)';
      params.push(`%${query.query}%`, `%${query.query}%`);
    }

    if (query.type) {
      sql += ' AND type = ?';
      params.push(query.type);
    }

    if (query.category) {
      sql += ' AND category = ?';
      params.push(query.category);
    }

    if (query.author) {
      sql += ' AND author = ?';
      params.push(query.author);
    }

    if (query.tags && query.tags.length > 0) {
      sql += ' AND (';
      sql += query.tags.map(() => 'tags LIKE ?').join(' OR ');
      sql += ')';
      query.tags.forEach(tag => params.push(`%"${tag}"%`));
    }

    sql += ' ORDER BY downloads DESC, created_at DESC';

    if (query.limit) {
      sql += ' LIMIT ?';
      params.push(query.limit);
      
      if (query.offset) {
        sql += ' OFFSET ?';
        params.push(query.offset);
      }
    }

    const rows = this.db.prepare(sql).all(...params) as any[];
    
    // 加载完整数据
    const items: RegistryItem[] = [];
    for (const row of rows) {
      const item = await this.get(row.name);
      if (item) {
        items.push(item);
      }
    }

    return items;
  }

  /**
   * 应用 Registry 项目到本地
   */
  async apply(name: string, options: ApplyOptions): Promise<void> {
    const item = await this.get(name);
    if (!item) {
      throw new Error(`Registry item '${name}' not found`);
    }

    // 验证环境变量
    if (item.environment) {
      for (const [key, envVar] of Object.entries(item.environment)) {
        const value = options.variables?.[key] || envVar.default;
        
        if (envVar.required && !value) {
          throw new Error(`Required environment variable '${key}' not provided`);
        }
        
        if (value && envVar.pattern) {
          const regex = new RegExp(envVar.pattern);
          if (!regex.test(value)) {
            throw new Error(`Environment variable '${key}' does not match pattern: ${envVar.pattern}`);
          }
        }
      }
    }

    // 创建目标目录
    const targetDir = path.resolve(options.targetPath);
    fs.ensureDirSync(targetDir);

    // 复制文件
    for (const file of item.files) {
      const targetPath = path.join(targetDir, file.path);
      
      // 检查是否覆盖
      if (fs.existsSync(targetPath) && !options.overwrite) {
        if (options.interactive) {
          // TODO: 实现交互式确认
          console.warn(`File ${file.path} already exists, skipping...`);
          continue;
        } else {
          throw new Error(`File ${file.path} already exists. Use --overwrite to replace.`);
        }
      }

      fs.ensureDirSync(path.dirname(targetPath));

      // 处理模板变量
      let content = file.content;
      if (file.type === 'template' && options.variables) {
        for (const [key, value] of Object.entries(options.variables)) {
          content = content.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), value);
          content = content.replace(new RegExp(`\\$${key}`, 'g'), value);
        }
      }

      // 写入文件
      if (file.encoding === 'base64') {
        fs.writeFileSync(targetPath, Buffer.from(content, 'base64'));
      } else {
        fs.writeFileSync(targetPath, content, 'utf8');
      }

      // 设置权限
      if (file.permissions) {
        fs.chmodSync(targetPath, file.permissions);
      }
    }

    // 记录使用
    this.recordUsage(name, options);

    // 更新下载计数
    this.db.prepare('UPDATE registry_items SET downloads = downloads + 1 WHERE name = ?').run(name);
  }

  /**
   * 获取统计信息
   */
  async getStats(): Promise<RegistryStats> {
    const totalItems = this.db.prepare('SELECT COUNT(*) as count FROM registry_items').get() as any;
    
    const byType = this.db.prepare(`
      SELECT type, COUNT(*) as count 
      FROM registry_items 
      GROUP BY type
    `).all() as any[];

    const byCategory = this.db.prepare(`
      SELECT category, COUNT(*) as count 
      FROM registry_items 
      GROUP BY category
    `).all() as any[];

    const recentlyAdded = this.db.prepare(`
      SELECT * FROM registry_items 
      ORDER BY created_at DESC 
      LIMIT 10
    `).all() as any[];

    const mostUsed = this.db.prepare(`
      SELECT name, downloads 
      FROM registry_items 
      WHERE downloads > 0
      ORDER BY downloads DESC 
      LIMIT 10
    `).all() as any[];

    // 计算流行标签
    const allItems = this.db.prepare('SELECT tags FROM registry_items').all() as any[];
    const tagCounts: Record<string, number> = {};
    
    for (const item of allItems) {
      const tags = JSON.parse(item.tags) as string[];
      for (const tag of tags) {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      }
    }

    const popularTags = Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    return {
      totalItems: totalItems.count,
      byType: Object.fromEntries(byType.map(r => [r.type, r.count])),
      byCategory: Object.fromEntries(byCategory.map(r => [r.category, r.count])),
      popularTags,
      recentlyAdded: await Promise.all(
        recentlyAdded.map((row: any) => this.get(row.name))
      ).then(items => items.filter(Boolean) as RegistryItem[]),
      mostUsed: await Promise.all(
        mostUsed.map(async (row: any) => ({
          item: await this.get(row.name) as RegistryItem,
          uses: row.downloads
        }))
      )
    };
  }

  private rowToRegistryItem(row: any): RegistryItem {
    return {
      name: row.name,
      type: row.type,
      version: row.version,
      description: row.description,
      author: row.author,
      license: row.license,
      category: row.category,
      tags: JSON.parse(row.tags),
      files: [], // 需要从文件系统加载
      dependencies: row.dependencies ? JSON.parse(row.dependencies) : undefined,
      environment: row.environment ? JSON.parse(row.environment) : undefined,
      scripts: row.scripts ? JSON.parse(row.scripts) : undefined,
      metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
      targets: row.targets ? JSON.parse(row.targets) : undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  private recordUsage(itemName: string, options: ApplyOptions): void {
    this.db.prepare(`
      INSERT INTO registry_usage (item_name, applied_at, variables)
      VALUES (?, ?, ?)
    `).run(
      itemName,
      new Date().toISOString(),
      JSON.stringify(options.variables || {})
    );
  }
}